// LoginForm.tsx
// This component is a login form that allows users to sign in using their email or mobile number and otp.
// It includes validation for the input fields and a checkbox for accepting terms and conditions.
// The form uses React Hook Form for form management and Zod for validation.
// It also includes a dialog for displaying the terms and conditions, which users must accept before proceeding with the login.
// The form submits the data to an API endpoint and handles the response accordingly.

// Import necessary libraries and components
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
import axios from "../lib/axios";
import { useAuth } from "@/contexts/AuthContext";
// const apiBase = import.meta.env.VITE_API_BASE_URL;
import { API_BASE_URL } from '../config';
import MaskedMobileInput from "@/components/MaskedMobileInput";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { keepPreviousData } from '@tanstack/react-query';
import CallbackHelp from "@/components/CallbackHelp";


const formSchema = z.object({
  username: z.string().regex(/^\d{10}$/, { message: "Enter a valid 10-digit mobile number" }),
  otp: z.string().min(6, { message: "Please enter a valid 6 digit OTP" }),
  accepts_terms_and_privacy: z.boolean(),
}).refine(data => {
  // If not admin, terms must be accepted
  return true; // We'll handle specific validation in the component or via refined schema if needed
}, {
  message: "You must accept the terms and conditions",
  path: ["accepts_terms_and_privacy"]
});

type FormValues = z.infer<typeof formSchema>;

interface LoginFormProps {
  isAdminMode?: boolean;
  isWhitespace?: boolean;
}

const LoginForm = ({ isAdminMode = false, isWhitespace = false }: LoginFormProps) => {
  // add new state
  const [otpRequested, setOtpRequested] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);
  const [errorModalTitle, setErrorModalTitle] = useState<string>('Error');
  const [breErrorMessage, setBreErrorMessage] = useState<string | null>(null);
  const [breErrorDetails, setBreErrorDetails] = useState<any[]>([]);
  const [preapprovedRestaurants, setPreapprovedRestaurants] = useState<any[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const [isCallbackDialogOpen, setIsCallbackDialogOpen] = useState(false);

  const { login, adminLogin } = useAuth();
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      otp: '',
      accepts_terms_and_privacy: false,
    },
  });

  // add an effect after form declaration
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!values.username) {
        // Only reset OTP if it’s not already empty
        if (values.otp !== "") {
          form.setValue("otp", "");
        }

        // Only update OTP UI if needed
        if (otpRequested) {
          setOtpRequested(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, otpRequested]);

  useEffect(() => {
    if (otpRequested) {
      form.setFocus("otp");
    }
  }, [otpRequested, form.setFocus]);

  useEffect(() => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();

      navigator.credentials.get({
        otp: { transport: ["sms"] },
        signal: ac.signal,
      } as any).then((otp: any) => {
        if (otp?.code) {
          console.log("OTP received via WebOTP:", otp.code);
          form.setValue("otp", otp.code, { shouldValidate: true, shouldDirty: true }); // Autofill OTP with validation
          form.setFocus("otp"); // Ensure focus
        }
      }).catch(err => {
        console.log("WebOTP error:", err);
      });

      return () => ac.abort();
    }
  }, [form]);

  const onSubmit = async (values: FormValues) => {
    try {
      console.log(values)
      if (isAdminMode) {
        await adminLogin({ username: values.username, otp: values.otp });
        // Admin redirect handled by AuthContext or simplified here
        window.location.href = '/admin-dashboard';
        return;
      }

      // Manual check for terms acceptance (since schema refinement might be tricky with isAdminMode prop)
      if (!values.accepts_terms_and_privacy) {
        form.setError("accepts_terms_and_privacy", {
          type: "manual",
          message: "You must accept the terms and conditions."
        });
        return;
      }

      // Retrieve BAP info from sessionStorage if this is a whitespace journey
      const whitespace_bap_id = sessionStorage.getItem('whitespace_bap_id');
      const whitespace_bap_metadata = sessionStorage.getItem('whitespace_bap_metadata');

      // Build login payload with optional BAP information
      const loginPayload: any = {
        username: values.username,
        otp: values.otp,
        accepts_terms_and_privacy: values.accepts_terms_and_privacy,
      };

      // Include BAP information if present (whitespace journey)
      if (whitespace_bap_id) {
        loginPayload.bap_id = whitespace_bap_id;
        console.log('[LoginForm] Submitting login with BAP ID:', whitespace_bap_id);
      }

      if (whitespace_bap_metadata) {
        try {
          loginPayload.bap_metadata = JSON.parse(whitespace_bap_metadata);
        } catch (e) {
          console.warn('[LoginForm] Failed to parse BAP metadata:', e);
        }
      }

      // Submit login and capture response (includes BAP details from backend)
      const loginResponse: any = await login(loginPayload);

      // Extract and store BAP details from backend response
      if (loginResponse && loginResponse.bap_details) {
        const bapDetails = loginResponse.bap_details;
        sessionStorage.setItem('whitespace_bap_id', bapDetails.name);
        sessionStorage.setItem('whitespace_bap_logo', bapDetails.logo);
        sessionStorage.setItem('whitespace_bap_metadata', JSON.stringify(bapDetails));
        console.log('[LoginForm] BAP details from backend:', bapDetails);
      }

      // After login, fetch journey state and navigate to the appropriate route
      // This preserves the user's progress instead of always going to /welcome
      try {
        // Fetch journey state
        const res = await axios.post(`${API_BASE_URL}/api/journey/state/`);
        const journeyState = res.data;

        // Use dynamic URLs from backend
        let targetRoute = "/welcome"; // default

        if (journeyState.next_page_url) {
          targetRoute = journeyState.next_page_url;
        } else if (journeyState.current_page_url) {
          targetRoute = journeyState.current_page_url;
        }

        // Special handling: If user is at OTP_LOGIN, navigate to welcome (new user flow)
        if (journeyState.current_stage === "OTP_LOGIN") {
          targetRoute = "/welcome";
        }

        // Navigate to the appropriate route
        window.location.href = targetRoute;
      } catch (journeyErr) {
        // If journey state fetch fails, default to welcome
        console.error("Failed to fetch journey state:", journeyErr);
        window.location.href = '/welcome';
      }
    } catch (err) {
      alert(getErrorMessage(err));
      // setOtpRequested(false); // reset OTP state if login fails
      form.reset({}, {
        keepErrors: false,
        keepDirty: false,
        keepIsValid: false,
      });
      return
    }
  };

  const handleTermsAccept = () => {
    form.setValue('accepts_terms_and_privacy', true);
    setIsTermsDialogOpen(false);
  };

  const generateOTP = async () => {
    const data: any = form.getValues();

    // Retrieve BAP info from sessionStorage if this is a whitespace journey
    const whitespace_bap_id = sessionStorage.getItem('whitespace_bap_id');
    const whitespace_bap_metadata = sessionStorage.getItem('whitespace_bap_metadata');

    if (whitespace_bap_id) {
      data.bap_id = whitespace_bap_id;
    }
    if (whitespace_bap_metadata) {
      try {
        data.bap_metadata = JSON.parse(whitespace_bap_metadata);
      } catch (e) {
        // ignore parse error
      }
    }

    console.log(data);
    try {
      const endpoint = isAdminMode
        ? `${API_BASE_URL}/api/admin/request_otp/`
        : `${API_BASE_URL}/api/validate_generate_send_otp/`;

      const res = await axios.post(endpoint, data);
      setOtpRequested(true);

      // ── Pre-approved multi-restaurant handling ──────────────────────────
      // Backend returns { otp_sent: true, preapproved_restaurants: [...] }
      // when the phone is associated with more than one pre-approved restaurant.
      // Store the list so the UI can render a dropdown.
      if (res.data && Array.isArray(res.data.preapproved_restaurants)) {
        if (res.data.preapproved_restaurants.length >= 1) {
          setPreapprovedRestaurants(res.data.preapproved_restaurants);
          // Pre-select the first option only on first request (not resend)
          setSelectedRestaurantId((prev) =>
            prev !== null ? prev : (res.data.preapproved_restaurants[0].restaurant_id ?? null)
          );
          console.log('[LoginForm] Pre-approved restaurants:', res.data.preapproved_restaurants);
        } else {
          // Backend explicitly returned an empty list — clear state
          setPreapprovedRestaurants([]);
          setSelectedRestaurantId(null);
        }
      }
      // If preapproved_restaurants is absent from response (e.g. resend OTP),
      // leave existing preapprovedRestaurants state untouched.

      // Helper to focus
      setTimeout(() => {
        form.setFocus("otp");
      }, 100);

      if (res.data.message) {
        // Optional: if the message itself is an alert, we might leave it or use toast
        // alert(res.data.message); 
      }
    }
    catch (err) {
      alert(getErrorMessage(err));
    }
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-6 animate-fade-in">
        <h2 className="text-2xl font-bold text-intellend-green mb-6">Sign In</h2>
        {isWhitespace && (
          <p className="text-sm text-gray-600 mb-6 -mt-4 italic">Only for the owner of the business</p>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

            <Controller
              control={form.control}
              name="username"
              render={({ field }) => <MaskedMobileInput {...field} />}
            />



            {otpRequested && (
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>OTP</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter your OTP"
                        autoComplete="one-time-code"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {otpRequested && !isAdminMode && (
              <FormField
                control={form.control}
                name="accepts_terms_and_privacy"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <input id="tnc" type="checkbox" checked={field.value} onChange={field.onChange} />
                      <FormLabel htmlFor='tnc' className="text-sm">
                        I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Terms & Conditions</a> and <a href="/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <div
              className={`flex flex-col md:grid md:grid-cols-2 gap-4 ${otpRequested ? "flex-col-reverse" : "flex-col"
                }`}
            >
              <Button
                type="button"
                variant="outline"
                className="w-full border border-intellend-green text-intellend-green bg-transparent"
                onClick={generateOTP}
              >
                {otpRequested ? "Resend OTP" : "Request OTP"}
              </Button>
              <Button
                type="submit"
                className="w-full bg-intellend-green hover:bg-intellend-green/90"
                disabled={!otpRequested}
              >
                Sign In
              </Button>
            </div>
          </form>
        </Form>
      </div>

      {/* ── Request Callback Dialog ─────────────────────────────────────── */}
      {!isAdminMode && (
        <CallbackHelp
          isOpen={isCallbackDialogOpen}
          setIsOpen={setIsCallbackDialogOpen}
          prelogin={true}
          showCallUs={true}
          showWhatsappUs={true}
          silentlyTrigger={true}
          title="Request Callback"
          description="Enter your mobile number and our team will reach out to you shortly."
        />
      )}

      {/* ── Generic Error Modal (replaces alert()) ──────────────────────── */}
      <Dialog open={!!errorModalMessage} onOpenChange={(open) => { if (!open) setErrorModalMessage(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              ⚠️ {errorModalTitle}
            </DialogTitle>
            <DialogDescription className="text-base text-gray-700 pt-4 whitespace-pre-line">
              {errorModalMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              className="bg-intellend-green hover:bg-intellend-green/90"
              onClick={() => setErrorModalMessage(null)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── BRE Offer-Save Error Dialog ─────────────────────────────────── */}
      <Dialog open={!!breErrorMessage} onOpenChange={(open) => { if (!open) { setBreErrorMessage(null); setBreErrorDetails([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              ⚠️ Offer Save Error
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 mt-1">
              {breErrorMessage}
            </DialogDescription>
          </DialogHeader>

          {breErrorDetails.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Failed Rows ({breErrorDetails.length})
              </p>
              <ScrollArea className="h-64 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Lender</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {breErrorDetails.map((detail) => (
                      <TableRow key={detail.row}>
                        <TableCell className="font-mono text-xs">{detail.row}</TableCell>
                        <TableCell className="font-mono text-xs">{detail.contact_no || '—'}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" title={detail.business_name}>{detail.business_name || '—'}</TableCell>
                        <TableCell>
                          {detail.lender
                            ? <Badge variant="outline" className="text-xs">{detail.lender}</Badge>
                            : <span className="text-gray-400 text-xs">—</span>
                          }
                        </TableCell>
                        <TableCell className="text-xs text-red-600 max-w-[200px] break-words">{detail.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => { setBreErrorMessage(null); setBreErrorDetails([]); }}
            >
              Close
            </Button>
            <Button
              className="bg-intellend-green hover:bg-intellend-green/90"
              onClick={() => { setBreErrorMessage(null); setBreErrorDetails([]); window.location.reload(); }}
            >
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isTermsDialogOpen} onOpenChange={setIsTermsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Terms and Conditions</DialogTitle>
          </DialogHeader>
          <DialogDescription className="max-h-[60vh] overflow-auto">
            <div className="space-y-4 text-sm">
              <p>
                <strong>Welcome to Intellend</strong> - These Terms and Conditions govern your use of our service and provide information about our services.
              </p>
              <p>
                By accessing or using our services, you agree to these Terms and Conditions.
              </p>
              <h4 className="font-semibold">1. Acceptance of Terms</h4>
              <p>
                By accessing or using our platform, you agree to be bound by these Terms and Conditions. If you disagree with any part of the terms, you may not access our services.
              </p>
              <h4 className="font-semibold">2. Privacy Policy</h4>
              <p>
                Our Privacy Policy describes how we handle the information you provide to us when you use our services. You understand that by using our services, you consent to the collection and use of this information.
              </p>
              <h4 className="font-semibold">3. User Accounts</h4>
              <p>
                When you create an account with us, you must provide accurate and complete information. You are responsible for safeguarding your account credentials and for all activities that occur under your account.
              </p>
              <h4 className="font-semibold">4. Financial Services</h4>
              <p>
                The financial products and services available through our platform are subject to eligibility criteria and approval processes. We do not guarantee approval for any financial product.
              </p>
              <h4 className="font-semibold">5. Limitation of Liability</h4>
              <p>
                In no event shall Intellend, its officers, directors, employees, or agents be liable for any indirect, punitive, incidental, special, or consequential damages arising out of, or in any way connected with, your use of our services.
              </p>
            </div>
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTermsDialogOpen(false)}>Close</Button>
            <Button className="bg-intellend-green hover:bg-intellend-green/90" onClick={handleTermsAccept}>I Accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginForm;
